import React from 'react';

import { classnames, padding, useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiClose, mdiInformationOutline } from '@lumapps/lumx/icons';
import {
    Alignment,
    Checkbox,
    ColorPalette,
    Emphasis,
    FlexBox,
    Heading,
    Icon,
    IconButton,
    Orientation,
    Placement,
    Popover,
    Size,
    Text,
    Theme,
    Typography,
} from '@lumapps/lumx/react';
import { getKeyForLanguage, GLOBAL, useTranslate } from '@lumapps/translations';
import { useId } from '@lumapps/utils/hooks/useId';

import { DESIGNER } from '../../keys';

import './index.scss';

export interface ContentLanguagePopoverProps {
    inputLanguage: string;
    anchorRef: React.RefObject<HTMLButtonElement>;
    isOpen: boolean;
    onClose: (skipPopover: boolean) => void;
}

const CLASSNAME = 'content-language-popover';

/**
 *  Display a popover on content edition to warn the user of his current edition language
 */
export const ContentLanguagePopover: React.FC<ContentLanguagePopoverProps> = ({
    isOpen,
    onClose,
    inputLanguage,
    anchorRef,
}) => {
    const { translateKey, translateAndReplace } = useTranslate();
    const [skipPopover, setSkipPopover] = React.useState(false);
    const { block, element } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(CLASSNAME);
    const titleId = useId();

    return (
        <Popover
            role="dialog"
            aria-modal="true"
            className={block()}
            anchorRef={anchorRef}
            aria-labelledby={titleId}
            fitWithinViewportHeight
            focusAnchorOnClose
            closeOnClickAway={false}
            closeOnEscape
            hasArrow
            isOpen={isOpen}
            onClose={() => onClose(skipPopover)}
            placement={Placement.BOTTOM}
            theme={Theme.dark}
            withFocusTrap
        >
            <FlexBox className={classnames(padding('all', Size.big))} gap={Size.big} orientation={Orientation.vertical}>
                <FlexBox
                    orientation={Orientation.horizontal}
                    gap={Size.regular}
                    hAlign={Alignment.center}
                    vAlign="space-between"
                >
                    <FlexBox orientation={Orientation.horizontal} gap={Size.big} hAlign={Alignment.center}>
                        <Icon icon={mdiInformationOutline} size="s" />
                        <Heading as="h3" typography="title" id={titleId}>
                            {translateKey(DESIGNER.CONTENT_LANGUAGE)}
                        </Heading>
                    </FlexBox>
                    <IconButton
                        className={element('close')}
                        color={ColorPalette.light}
                        emphasis={Emphasis.low}
                        icon={mdiClose}
                        label={translateKey(GLOBAL.CLOSE)}
                        onClick={() => onClose(skipPopover)}
                        size={Size.m}
                        tooltipProps={{ placement: Placement.TOP }}
                        {...get({ element: 'button', action: 'close-toggle-tip' })}
                    />
                </FlexBox>
                <Text as="p" color={ColorPalette.light} typography={Typography.body1}>
                    {translateAndReplace(DESIGNER.EDITION_LANGUAGE_WARNING, {
                        LANG: translateKey(getKeyForLanguage(inputLanguage)),
                    })}
                </Text>
                <Checkbox
                    onChange={setSkipPopover}
                    isChecked={skipPopover}
                    label={translateKey(GLOBAL.DO_NOT_SHOW_THIS_MESSAGE_AGAIN)}
                />
            </FlexBox>
        </Popover>
    );
};
